/**
 * Protected Route Component
 * Guards routes requiring authentication and specific roles
 */

import { useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';

export interface ProtectedRouteProps {
  requiredRole?: 'ADMIN' | 'CATERER' | Array<'ADMIN' | 'CATERER'>;  // Optional role restriction (single or multiple)
}

/**
 * Protected Route Guard
 * Checks authentication and role permissions
 * Redirects to login if not authenticated
 * Redirects to appropriate dashboard if role doesn't match
 */
export function ProtectedRoute({ requiredRole }: ProtectedRouteProps) {
  const { isAuthenticated, user, isLoading, refreshUser } = useAuthStore();
  const location = useLocation();
  const [initialCheckDone, setInitialCheckDone] = useState(false);

  // On mount, verify session with server
  useEffect(() => {
    const checkAuth = async () => {
      if (!user && !isLoading) {
        await refreshUser();
      }
      setInitialCheckDone(true);
    };

    checkAuth();
  }, []); // Only run once on mount

  // Re-check on navigation (throttled): pages that only read public data never 401
  useEffect(() => {
    if (initialCheckDone && useAuthStore.getState().user) {
      refreshUser({ silent: true, maxAgeMs: 15_000 });
    }
  }, [location.pathname, initialCheckDone, refreshUser]);

  // Re-check when the tab comes back into view (session may have ended while idle)
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible' && useAuthStore.getState().user) {
        refreshUser({ silent: true, maxAgeMs: 60_000 });
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [refreshUser]);

  // Show loading spinner while checking auth (initial check OR explicit loading)
  if (!initialCheckDone || isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Not authenticated - redirect to login with return URL
  if (!isAuthenticated || !user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check role if required
  if (requiredRole) {
    const allowedRoles = Array.isArray(requiredRole) ? requiredRole : [requiredRole];
    if (!allowedRoles.includes(user.role as 'ADMIN' | 'CATERER')) {
      // Redirect to appropriate dashboard based on actual role
      switch (user.role) {
        case 'ADMIN':
          return <Navigate to="/admin/dashboard" replace />;
        case 'CATERER':
          return <Navigate to="/caterer/dashboard" replace />;
        case 'MEMBER':
        default:
          return <Navigate to="/rsvp" replace />;
      }
    }
  }

  // Render child routes
  return <Outlet />;
}
